/**
 * Autosave for draft clinical forms (ASM-03, Phase 4).
 *
 * Every `intervalMs` (default 30 s) the hook calls `save()` when `enabled` is true and the form is
 * dirty. A 409 from the server (record no longer a draft, chart locked, ...) stops autosave until
 * `resetKey` changes, and is reported through `error` and `onError`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isApiError } from '../dtos/common/api.dto';

export const AUTOSAVE_INTERVAL_MS = 30_000;

export interface UseAutosaveOptions {
  /** Persists the current form state. Rejects on failure. */
  save: () => Promise<unknown>;
  /** Whether there are unsaved changes; a function is read at tick time. */
  isDirty: boolean | (() => boolean);
  /** Tick interval in ms (default 30 000) */
  intervalMs?: number;
  /** Autosave runs only while true (e.g. the record is a DRAFT and the user can edit) */
  enabled: boolean;
  /** Called on a failed autosave; `stopped` is true when autosave has stopped (409). */
  onError?: (error: unknown, stopped: boolean) => void;
  /** Changing this value clears a 409 stop and the last error (e.g. record id + status). */
  resetKey?: unknown;
}

export interface UseAutosaveResult {
  lastSavedAt: Date | null;
  saving: boolean;
  error: unknown;
  /** True after a 409; autosave does not run again until `resetKey` changes */
  stopped: boolean;
  /** Record a successful manual save so the indicator stays accurate */
  markSaved: (at?: Date) => void;
}

const readDirty = (isDirty: boolean | (() => boolean)): boolean =>
  typeof isDirty === 'function' ? isDirty() : isDirty;

export function useAutosave({
  save,
  isDirty,
  intervalMs = AUTOSAVE_INTERVAL_MS,
  enabled,
  onError,
  resetKey,
}: UseAutosaveOptions): UseAutosaveResult {
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [stopped, setStopped] = useState(false);

  // Latest values without restarting the interval
  const saveRef = useRef(save);
  const dirtyRef = useRef(isDirty);
  const onErrorRef = useRef(onError);
  const inFlightRef = useRef(false);
  const stoppedRef = useRef(false);
  const mountedRef = useRef(true);

  saveRef.current = save;
  dirtyRef.current = isDirty;
  onErrorRef.current = onError;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    setStopped(false);
    setError(null);
  }, [resetKey]);

  const tick = useCallback(async () => {
    if (inFlightRef.current || stoppedRef.current) return;
    if (!readDirty(dirtyRef.current)) return;

    inFlightRef.current = true;
    setSaving(true);
    try {
      await saveRef.current();
      if (!mountedRef.current) return;
      setLastSavedAt(new Date());
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      const isConflict = isApiError(err) && err.statusCode === 409;
      if (isConflict) {
        stoppedRef.current = true;
        setStopped(true);
      }
      setError(err);
      onErrorRef.current?.(err, isConflict);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => {
      void tick();
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [enabled, intervalMs, tick]);

  const markSaved = useCallback((at?: Date) => {
    setLastSavedAt(at ?? new Date());
    setError(null);
  }, []);

  return { lastSavedAt, saving, error, stopped, markSaved };
}

/** "Saved 2:05 PM" style label for the autosave indicator */
export function formatSavedTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default useAutosave;
